<template>
  <vinnuskoli-page>
    <b-row>
      <b-col>
        <div class="page-title">Samþykkja umsóknir</div>
      </b-col>
      <b-col>
        <b-btn variant="secondary" :to="{ name: 'ApplicationList' }" class="pull-right"> <i class="fa fa-fw fa-reply"></i> Til baka </b-btn>
      </b-col>
    </b-row>
    <div>
      <div v-if="loading || loadingGroups">
        <i class="fa fa-spin fa-spinner"></i>
      </div>
      <div v-for="periodGroup in periodGrouping" :key="periodGroup.id" v-else>
        <hr />
        <h4 class="mt-4 mb-3">
          {{ periodGroup.name }}
          <small class="text-muted">
            {{ periodGroup.dateFrom | moment('DD.MM.YYYY') }} - {{ periodGroup.dateTo | moment('DD.MM.YYYY') }}
          </small>
        </h4>
        <template v-if="periodGroup.applicationGrouping.length > 0">
          <hr />
          <approve-application-item :periodGroup="periodGroup" :ref="`periodGroupItem`" />
        </template>

        <div v-for="periodGroupingPart in periodGroup.periodGroupingParts" :key="`${periodGroup.id}${periodGroupingPart.id}`">
          <hr />
          <h5 class="mt-3 mb-3">
            {{ periodGroupingPart.dateFrom | moment('DD.MM.YYYY') }} - {{ periodGroupingPart.dateTo | moment('DD.MM.YYYY') }}
          </h5>
          <hr />

          <approve-application-item
            :periodGroup="periodGroupingPart"
            v-if="periodGroupingPart.applicationGrouping.length > 0"
            :ref="`periodGroupItem`"
          />
        </div>
      </div>
    </div>
    <b-row class="mt-3">
      <b-col>
        <b-button variant="secondary" :to="{ name: 'ApplicationList' }" :disabled="saving">
          <span v-if="saved"><i class="fa fa-fw fa-reply"></i> Til baka á yfirlit</span
          ><span v-else><i class="fa fa-remove"></i> Hætta við</span>
        </b-button>
        <b-button v-if="!saved" variant="success" @click.prevent="validateBeforeSubmit" :disabled="saving">
          <i class="fa fa-check"></i>
          Staðfesta
        </b-button>
      </b-col>
    </b-row>
  </vinnuskoli-page>
</template>

<script>
import { mapActions } from 'vuex';
import core from '@/api/core';

import ApproveApplicationItem from './ApproveApplicationItem.vue';

export default {
  name: 'approve-application-modal',
  data() {
    return {
      loading: false,
      loadingGroups: false,
      saving: false,
      saved: false,
      periodGrouping: null,
    };
  },
  components: {
    ApproveApplicationItem,
  },
  methods: {
    async loadApplications() {
      this.loading = true;
      try {
        const response = await this.$applicationApi.applicationList({
          ids: this.$route.params.id.split(','),
          status: ['NEW', 'SEMI_ACCEPTED', 'IN_PROGRESS', 'IN_PROGRESS_APPROVED'],
          limit: 100,
        });
        await this.processApplications(response.data);
      } catch (e) {
        this.$log.error(e);
        this.displayError(`Óvænt villa kom upp. Vinsamlegast reyndu aftur.`);
      } finally {
        this.loading = false;
      }
    },
    async processApplications(applications) {
      this.loadingGroups = true;
      try {
        const promises = [];
        // Setja hópa á applications. Þurfum að gera það sync hérna svo það rati allt inn
        // í næstu ítranir.
        /* eslint-disable no-param-reassign */
        applications.forEach((a, aIndex) => {
          if (a.periodParts && a.periodParts.length > 0) {
            a.periodParts.forEach((p, pIndex) => {
              if (p.status === 'NEW') {
                promises.push(
                  this.getGroups(a, p).then(availableGroups => {
                    applications[aIndex].periodParts[pIndex].availableGroups = availableGroups;
                  }),
                );
              }
            });
          } else if (a.status === 'NEW' || a.status === 'IN_PROGRESS' || a.status === 'IN_PROGRESS_APPROVED') {
            promises.push(
              this.getGroups(a, null).then(availableGroups => {
                applications[aIndex].availableGroups = availableGroups;
              }),
            );
          }
        });
        /* eslint-enable */

        await Promise.all(promises);

        // Safna saman öllum unique tímabilum í eitt array.
        const periodGrouping = applications
          .map(a => a.period)
          .filter((v, i, a) => a.findIndex(v2 => v.id === v2.id) === i)
          .map(p => ({
            applicationGrouping: [],
            periodGroupingParts: [],
            ...p,
          }));

        // Safna saman öllum unique starfstímabilum inn í tímabilin.
        periodGrouping.forEach(pg => {
          pg.periodGroupingParts.push(
            ...applications
              .filter(a => a.period.id === pg.id)
              .flatMap(a => a.periodParts)
              .map(p => p.periodPart)
              .filter((v, i, a) => a.findIndex(v2 => v.id === v2.id) === i)
              .map(p => ({
                applicationGrouping: [],
                ...p,
              })),
          );
        });

        // Safna saman öllum umsóknum inn í tímabil og starfstímabil.
        periodGrouping.forEach(pg => {
          // Tímabil
          applications
            .filter(a => !a.periodParts || a.periodParts.length === 0)
            .filter(a => a.period.id === pg.id)
            .forEach(a => pg.applicationGrouping.push(a));

          // Starfstímabil
          pg.periodGroupingParts.forEach(pg2 => {
            applications
              .filter(a => a.periodParts && a.periodParts.length > 0)
              .forEach(a => {
                a.periodParts
                  .filter(ap => ap.periodPart.id === pg2.id)
                  .forEach(ap =>
                    pg2.applicationGrouping.push({
                      ...a,
                      periodGroupingPart: ap,
                    }),
                  );
              });
          });
        });

        this.periodGrouping = periodGrouping;
      } finally {
        this.loadingGroups = false;
      }
    },
    async getGroups(application, periodPart) {
      let response;
      if (periodPart) {
        response = await core.dropdownApplicationPlacementgroupList({
          period: application.period.id,
          period_part: periodPart.periodPart.id,
          workplaces: application.workplaces.map(x => x.workplace.id).join(),
          type: null,
          birth_year: application.birthYear.birthYear,
        });
      } else {
        response = await core.dropdownApplicationPlacementgroupList({
          period: application.period.id,
          workplaces: application.workplaces.map(x => x.workplace.id).join(),
          type: null,
          birth_year: application.birthYear.birthYear,
        });
      }

      return response.data;
    },
    async validateBeforeSubmit() {
      try {
        this.saving = true;

        const results = await Promise.all(this.$refs.periodGroupItem.map(r => r.doApprove()));

        const totalFailures = results.reduce((sum, count) => sum + count, 0);
        if (totalFailures === 0) {
          this.saved = true;
          this.displaySuccess('Umsóknir samþykktar');
        }
        // this.$router.push({ name: 'ApplicationList' });
      } finally {
        this.saving = false;
      }
    },
    ...mapActions({
      displayError: 'displayError',
      displaySuccess: 'displaySuccess',
    }),
  },
  created() {
    this.loadApplications();
  },
};
</script>
